import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '../services/api/auth'

export interface Admin {
  id: number
  username: string
  email: string | null
  is_active: boolean
  is_superuser: boolean
  last_login_at: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // 토큰 초기 로드: localStorage(영구) 우선, 없으면 sessionStorage(탭 한정)
  const token = ref<string | null>(
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token')
  )
  const admin = ref<Admin | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value)
  const isSuperuser = computed(() => admin.value?.is_superuser ?? false)

  async function login(username: string, password: string, keepLoggedIn: boolean = false) {
    isLoading.value = true
    error.value = null

    try {
      const response = await authApi.login(username, password, keepLoggedIn)
      token.value = response.access_token

      // 양쪽 storage를 먼저 비우고, 선택된 storage에만 저장
      localStorage.removeItem('access_token')
      sessionStorage.removeItem('access_token')
      const storage = keepLoggedIn ? localStorage : sessionStorage
      storage.setItem('access_token', response.access_token)

      await fetchCurrentUser()
      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || '로그인에 실패했습니다'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function fetchCurrentUser() {
    if (!token.value) return

    try {
      const user = await authApi.getCurrentUser()
      admin.value = user
    } catch (err) {
      logout()
    }
  }

  async function updateProfile(email: string) {
    isLoading.value = true
    error.value = null

    try {
      const updated = await authApi.updateProfile({ email })
      admin.value = updated
      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || '프로필 업데이트에 실패했습니다'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function changePassword(oldPassword: string, newPassword: string) {
    isLoading.value = true
    error.value = null

    try {
      await authApi.changePassword({
        old_password: oldPassword,
        new_password: newPassword,
      })
      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || '비밀번호 변경에 실패했습니다'
      return false
    } finally {
      isLoading.value = false
    }
  }

  function logout() {
    token.value = null
    admin.value = null
    localStorage.removeItem('access_token')
    sessionStorage.removeItem('access_token')
  }

  async function initialize() {
    if (token.value) {
      await fetchCurrentUser()
    }
  }

  return {
    token,
    admin,
    isLoading,
    error,
    isAuthenticated,
    isSuperuser,
    login,
    logout,
    fetchCurrentUser,
    updateProfile,
    changePassword,
    initialize,
  }
})
